import { Inject, Injectable } from '@angular/core';
import { Call, Device } from '@twilio/voice-sdk';
import { ApiHttpClient } from 'app/http/clients/api';
import { Observable, BehaviorSubject, lastValueFrom, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Select, Store } from '@ngxs/store';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { RollbarService } from 'app/rollbar';
import Rollbar from 'rollbar';
import { PushCallAlert, UpdateCall } from 'app/store/call/call.actions';
import { CallUpdates } from 'app/models/dto/call.model';

interface AccessTokenResponseData {
    token: string;
}

@Injectable({
    providedIn: 'root'
})
export class TwilioDeviceService {

    @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

    connected$: Observable<boolean>;
    user: LenderUser;

    private twilioDevice;
    private twilioCall;
    private connectedBS = new BehaviorSubject(false);
    private twilioErrorAlerted: boolean = false;

    constructor(
        @Inject(RollbarService) private rollbar: Rollbar,
        private api: ApiHttpClient,
        private store: Store
    ) {
        this.connected$ = this.connectedBS.asObservable();
    }

    connected(): boolean {
        const connected = !!this.twilioDevice;
        return connected;
    }

    async isSoftphoneEnabled(softphoneType: string) {
        this.lenderUser$.pipe(take(1)).subscribe(user => {
            this.user = user;
        });
        let type: string | null;
        if (softphoneType === 'outbound') {
            type = 'forwardOutboundCallsTo';
        } else if (softphoneType === 'inbound') {
            type = 'forwardCallsTo';
        } else {
            type = null;
        }
        return type ? this.user.settings[type] === 'pipeline' : false;
    }

    async setup() {
        // bail if we already have a twilio device setup
        if (this.twilioDevice) {
            return;
        }

        if (
            location.hostname !== 'localhost'
            && location.protocol !== 'https:'
        ) {
            return;
        }

        const token = await lastValueFrom(this.getAccessToken(), { defaultValue: '' });
        // if token promise fails, and we default to ''
        if (!token) {
            this.store.dispatch(new PushCallAlert('info', 'The Twilio token setup had an error. Please refresh and try again.'));
            return;
        }

        this.twilioDevice = new Device(token, {
            closeProtection: true
        });

        this.twilioDevice.audio.incoming(false);
        this.twilioDevice.audio.disconnect(false);

        this.twilioDevice.on('error', (twilioError, call) => {
            const updates: CallUpdates = {
                end: true,
                disposition: 'disconnected'
            };
            // This will also clear the active call from state.
            this.store.dispatch(new UpdateCall(updates));
            if (!this.twilioErrorAlerted) {
                this.rollbar.error('Twilio Device - Error', {
                    'twilioError': twilioError,
                    'call': call
                });
                this.store.dispatch(new PushCallAlert('info', 'The Twilio connection had an error.'));
                this.twilioErrorAlerted = true;

                // To prevent duplicate alerts we will repress the alerts for a period of time
                setTimeout(function(){ 
                    this.twilioErrorAlerted = false; 
                }, 5000);
            }
            this.destroy();
        });

        this.twilioDevice.updateToken(token);

        this.twilioDevice.register().catch(error => {
            this.rollbar.error('Twilio Device - Registration Error', error);
            return;
        });

        await new Promise((resolve) => {
            this.twilioDevice.on('registered', (device) => {
                resolve(device);
            });
        });

        this.twilioDevice.on('incoming', (call: Call) => {
            this.incomingCall(call);
        });

        this.twilioDevice.on('tokenWillExpire', () => {
            this.refreshToken();
        });

        this.connectedBS.next(true);
    }

    incomingCall(call: Call) {
        this.twilioCall = call;
        this.twilioCall.accept();
        this.twilioCall.on('disconnect', () => {
            this.destroy();
        });
    }

    sendDigits(digits: string) {
        if (!this.twilioDevice) {
            return;
        }
        this.twilioCall.sendDigits(digits);
    }

    muteCall(setting: boolean) {
        if (!this.twilioDevice || !this.twilioCall) {
            return;
        }
        this.twilioCall.mute(setting);
    }

    isTwilioDeviceMuted(): boolean {
        if (!this.twilioDevice) {
            return false;
        }
        return this.twilioCall ? this.twilioCall.isMuted() : false;
    }

    async refreshToken() {
        if (!this.twilioDevice) {
            return;
        }

        const token = await lastValueFrom(this.getAccessToken(), { defaultValue: '' });
        // if token promise fails, and we default to ''
        if (!token) {
            this.store.dispatch(new PushCallAlert(
                'info', 'The Twilio connection token had an error updating. Please refresh and try again.'
            ));
            return;
        }

        this.twilioDevice.updateToken(token);
        return;
    }

    destroy() {
        if (!this.twilioDevice) {
            return;
        }

        // make way for next call by destroying twilio device
        this.twilioDevice.destroy();
        this.twilioDevice = null;
        this.twilioCall = null;
        this.connectedBS.next(false);
        return;
    }

    private getAccessToken(): Observable<string> {
        return this.api.post<AccessTokenResponseData>('/phone/access-token', {})
        .pipe(catchError(err => {
            // prompt user
            this.store.dispatch(new PushCallAlert('info', 'The Twilio connection token had an error. Please refresh and try again.'));
            // log to Rollbar
            this.rollbar.error('Twilio Device - Access Token Error', err);
            return of();
        }),
        map(data => data.token));
    }
}
